// ─── Design tokens — Setal Express ────────────────────────────────────────────
// Direction : « Clean Utility » — dense, professionnel, orienté données.
// Palette marine profond + ambre. Bordures nettes, ombres minimales.
// Toute nouvelle UI doit consommer ces tokens plutôt que des valeurs en dur.

export const C = {
  // ─── Surfaces ───────────────────────────────────────────────────────────
  bg:          "#f7f7f5",  // fond applicatif (blanc cassé chaud)
  surface:     "#ffffff",
  surfaceAlt:  "#fafaf9",  // lignes alternées, en-têtes de tableau
  surfaceHover:"#f4f4f2",

  // ─── Bordures ───────────────────────────────────────────────────────────
  border:      "#e4e4e1",
  borderStrong:"#d0d0cb",
  borderFocus: "#0a2540",

  // ─── Texte ──────────────────────────────────────────────────────────────
  ink:         "#0a2540",  // titres, texte principal
  inkBody:     "#3f4a58",  // paragraphes
  inkMuted:    "#64748b",  // labels, métadonnées
  inkFaint:    "#94a3b8",  // placeholders, texte désactivé
  inkOn:       "#ffffff",  // texte sur fond sombre

  // ─── Marque ─────────────────────────────────────────────────────────────
  navy:        "#0a2540",
  navyHover:   "#14395c",
  navyActive:  "#061a2e",
  navySoft:    "#eef2f6",  // fond de sélection / état actif discret

  // ─── Accent (ambre) ─────────────────────────────────────────────────────
  amber:       "#d97706",
  amberHover:  "#b45309",
  amberSoft:   "#fffbeb",
  amberBorder: "#fde68a",

  // ─── Sémantique ─────────────────────────────────────────────────────────
  success:     "#15803d",
  successSoft: "#f0fdf4",
  successBorder:"#bbf7d0",

  danger:      "#b91c1c",
  dangerHover: "#991b1b",
  dangerSoft:  "#fef2f2",
  dangerBorder:"#fecaca",

  warning:     "#b45309",
  warningSoft: "#fffbeb",
  warningBorder:"#fde68a",

  info:        "#1d4ed8",
  infoSoft:    "#eff6ff",
  infoBorder:  "#bfdbfe",
};

// ─── Rayons ───────────────────────────────────────────────────────────────────
export const R = { sm: 4, md: 6, lg: 8, xl: 12, pill: 999 };

// ─── Ombres — volontairement discrètes, la bordure porte la séparation ────────
export const SH = {
  none: "none",
  sm:   "0 1px 2px rgba(10,37,64,.05)",
  md:   "0 2px 6px rgba(10,37,64,.07)",
  lg:   "0 8px 24px rgba(10,37,64,.10)",
  pop:  "0 12px 32px rgba(10,37,64,.14)",
};

// ─── Espacement (échelle 4px) ─────────────────────────────────────────────────
export const S = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, "2xl": 24, "3xl": 32, "4xl": 40, "5xl": 56 };

// ─── Typographie ──────────────────────────────────────────────────────────────
export const F = {
  sans: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
  mono: `ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace`,
  // Échelle : dense en haut, généreuse pour les titres marketing
  size: { xs: 11, sm: 12, base: 13, md: 14, lg: 16, xl: 18, "2xl": 22, "3xl": 28, "4xl": 34 },
  weight: { normal: 400, medium: 500, semi: 600, bold: 700 },
};

// ─── Statuts de commande ──────────────────────────────────────────────────────
// Un point coloré + un fond discret. Pas de dégradé, pas d'ombre colorée.
export const STATUS = {
  "En attente":    { dot: "#a16207", bg: "#fefce8", border: "#fef08a", text: "#854d0e" },
  "Réceptionné":   { dot: "#b45309", bg: "#fffbeb", border: "#fde68a", text: "#92400e" },
  "Collecté":      { dot: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af" },
  "En traitement": { dot: "#6d28d9", bg: "#f5f3ff", border: "#ddd6fe", text: "#5b21b6" },
  "Prêt":          { dot: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" },
  "Livré":         { dot: "#0a2540", bg: "#eef2f6", border: "#cbd5e1", text: "#0a2540" },
  "Récupéré":      { dot: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" },
};

export function statusStyle(status) {
  return STATUS[status] || { dot: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0", text: "#475569" };
}

// ─── Rôles ────────────────────────────────────────────────────────────────────
export const ROLE_STYLE = {
  admin:     { dot: C.danger,  bg: C.dangerSoft,  border: C.dangerBorder,  text: C.danger },
  manager:   { dot: C.info,    bg: C.infoSoft,    border: C.infoBorder,    text: C.info },
  collector: { dot: C.success, bg: C.successSoft, border: C.successBorder, text: C.success },
};

// ─── Graphiques — teintes sobres, pas de fluo ─────────────────────────────────
export const CHART = ["#0a2540", "#1d4ed8", "#d97706", "#15803d", "#6d28d9", "#b91c1c", "#0e7490", "#a16207"];

// ─── Helper : chiffres alignés (indispensable en UI dense) ────────────────────
export const numeric = { fontVariantNumeric: "tabular-nums", fontFeatureSettings: '"tnum"' };

// ─── Helper : référence de commande / identifiant technique ───────────────────
export const monoRef = {
  fontFamily: F.mono,
  fontSize: F.size.base,
  letterSpacing: "-0.02em",
  fontVariantNumeric: "tabular-nums",
};
